import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import {
  Collapse,
  Button,
  Row,
  Col,
  Typography,
  Input,
  DatePicker,
  Tag,
  Result,
  Image,
  Tooltip,
  Badge,
  Segmented,
  Checkbox,
  Grid,
  InputNumber,
  Space,
} from 'antd';
import { PlusOutlined, MenuOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { FormProvider } from 'rc-field-form';
// import moment from 'moment';

import dayjs from 'dayjs';

import SurveyFormBasic from './SurveyFormBasic';
import SurveyFormAdvanced from './SurveyFormAdvanced';
import Media from './Media';
import SectionHeader from './SectionHeader';
import QuestionerSlider from './QuestionerSlider';
import usePrivateAxios from '../hooks/usePrivateAxios';
import validateObject from '../utils/validateObject';
import StatusNotification from './StatusNotification';
import LoadingIcon from './LoadingIcon';
import GlobalColors from '../assets/colors/GlobalColors';
import GenerateSurveys from './GenerateSurveys';
import ClosingMessageInput from './ClosingMessageInput';
import extractValidationError from '../utils/extractValidationError';
import isObjectEmpty from '../utils/isObjectEmpty';
import getScreenSize from '../utils/getScreenSize';
import recalculateSubHeadingNumbering from '../utils/recalculateSubHeadingNumbering';
import update from 'immutability-helper';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider, useDrag, useDrop } from 'react-dnd';

import magic from '../assets/images/magic.svg';
import SubscriptionExpired from './SubscriptionExpired';

const { Panel } = Collapse;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const QuestionType = 'QUESTION';
const OptionType = 'OPTION';

const DraggableCollapse = ({
  id,
  index,
  moveItem,
  children,
  header,
  extra,
  activePanel,
  setOpen,
}) => {
  const ref = React.useRef(null);
  const [, drop] = useDrop({
    accept: QuestionType,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: QuestionType,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity, marginBottom: 16 }}>
      <Collapse
        activeKey={activePanel}
        onChange={(e) => {
          setOpen([e]);
        }}
        accordion
      >
        <Panel header={header} key={index + 1} extra={extra}>
          {children}
        </Panel>
      </Collapse>
    </div>
  );
};

const HandleSurvey = ({ action }) => {
  const [open, setOpen] = useState(['1']);
  const [questions, setQuestions] = useState([]);
  // const [surveyName, setSurveyName] = useState('');

  // const [startDateNew, setStartDateNew] = useState('');
  // const [endDateNew, setEndDateNew] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetKey, setResetKey] = useState(Date.now());

  const [isDateBeforeError, setIsDateBeforeError] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentMode, setCurrentMode] = useState('regular');

  const [typeCounter, setTypeCounter] = useState({
    openEnded: 1,
    optionList: 1,
    wordCount: 1,
  });

  const {
    user: { organizationId, registrationDate, planType, subscriptionStatus },

    surveysData: {
      create: {
        surveyId,
        surveyInputData,
        openEndeds,
        completed,
        emojis,
        numberOfQuestions,
        startDateNew,
        endDateNew,
        surveyName,
        media,
        formType,
        isLiveEvent,
        isQuiz,
        isResearch,
        optInMessage,
        testimonialMessage,
        isOptIn,
        isTestimonial,
        isWelcomeMessage,
        welcomeMessage,
        isClosingMessage,
        closingMessage,
        // settings: { isRepeatable = false, responseSize = -1, formBricksUrl = ' ' },
      },
    },
  } = useSelector((state) => state);

  const surveysData = useSelector((state) => state.surveysData) || {};
  const create = surveysData.create || {};
  const settings = create.settings || {};
  const { isRepeatable = false, responseSize = -1, formBricksUrl = '' } = settings;

  const isCloseURL = media?.closeURL || false;

  const history = useHistory();

  const screens = useBreakpoint();
  const screenSize = getScreenSize(screens);

  const dispatch = useDispatch();
  const axiosPrivate = usePrivateAxios();
  const apiURL = '/api/surveys';

  const today = dayjs(dayjs().format('YYYY-MM-DD'));
  const regday = dayjs(registrationDate).format('YYYY-MM-DD');
  const daysLeft = 7 - today.diff(regday, 'days');
  const toolTip = (value) => <span>{value}</span>;

  useEffect(() => {
    if (surveyInputData) {
      setOpen(null);
      const hydratedQuestions = Object.entries(surveyInputData)
        .filter(([key, value]) => {
          return (
            value &&
            typeof value === 'object' &&
            'mainQuestion' in value &&
            value.mainQuestion.trim() !== ''
          );
        })
        .map(([key, value]) => ({
          id: key,
          text: value.mainQuestion,
        }));
      setQuestions(hydratedQuestions);
    }
  }, [surveyInputData]);

  useEffect(() => {
    const newQuestions = Object.entries(surveyInputData)
      .slice(0, numberOfQuestions)
      .map(([key, value]) => ({
        id: key,
        text: value.mainQuestion,
      }));
    setQuestions(newQuestions);
  }, [numberOfQuestions, surveyInputData]);

  const handleSurveyTitle = (e) => {
    let { value: surveyTitle } = e.target;
    surveyTitle = surveyTitle?.toLowerCase().replaceAll(' ', '_');
    dispatch({ type: 'CREATE_SURVEY_NAME', data: surveyTitle });
    if (surveyTitle.length >= 5 && surveyTitle.length <= 25) {
      // setSurveyName(e.target.value);
      setTitleError(false);
    } else setTitleError(true);
  };

  const tagComplete = <Tag color='#87d068'>completed</Tag>;
  const tagDraft = <Tag color='#9E9E9E'>draft</Tag>;

  const hasErrors = () => {
    let e = false;

    if (!startDateNew || !endDateNew) {
      e = true;
      if (!startDateNew) setStartDateError(true);
      if (!endDateNew) setEndDateError(true);
    }

    if (startDateNew && endDateNew) {
      if (dayjs(endDateNew).isBefore(dayjs(startDateNew))) {
        e = true;
        setIsDateBeforeError(true);
      }
    }

    if (!surveyName || surveyName.length < 5 || surveyName.length > 25) {
      setTitleError(true);
      e = true;
    }

    return e;
  };

  const handleFormType = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_FORM_TYPE',
      data: e === 'basic' ? 'basic' : 'advanced',
    });
    if (e === 'advanced')
      dispatch({
        type: 'SET_SURVEY_MODE',
        data: { liveEvent: false, isQuiz: false, isResearch: currentMode === 'research' },
      });
  };

  const handleMode = (e) => {
    let payload;
    setCurrentMode(e);

    if (e === 'live') payload = { liveEvent: true, isQuiz: false, isResearch: false };
    else if (e === 'quiz') payload = { liveEvent: true, isQuiz: true, isResearch: false };
    else if (e === 'research') payload = { liveEvent: false, isQuiz: false, isResearch: true };
    else payload = { liveEvent: false, isQuiz: false, isResearch: false };

    dispatch({
      type: 'SET_SURVEY_MODE',
      data: payload,
    });
  };

  const handleWelcomeMessageCheckBox = (e) => {
    dispatch({ type: 'CREATE_SURVEY_SET_IS_WELCOME_MESSAGE', data: e.target.checked });
  };
  const handleClosingMessageCheckBox = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_IS_CLOSING_MESSAGE',
      data: e.target.checked,
    });
  };
  const handleWelcomeMessage = (e) => {
    dispatch({ type: 'CREATE_SURVEY_SET_WELCOME_MESSAGE', data: e.target.value });
  };

  const handleClosingMessage = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_CLOSING_MESSAGE',
      data: e.target.value,
    });
  };
  const handleOptInCheckBox = (e) => {
    dispatch({ type: 'CREATE_SURVEY_SET_IS_OPT_IN', data: e.target.checked });
  };
  const handleTestimonialCheckBox = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_IS_TESTIMONIAL',
      data: e.target.checked,
    });
  };

  const handleOptInMessage = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_MARKETING_OPT_IN_MESSAGE',
      data: e.target.value,
    });
  };

  const handleTestimonialMessage = (e) => {
    dispatch({
      type: 'CREATE_SURVEY_SET_TESTIMONIAL_MESSAGE',
      data: e.target.value,
    });
  };

  const handleIsRepeatableCheckBox = (e) => {
    dispatch({ type: 'CREATE_SURVEY_SET_IS_REPEATABLE', data: e.target.checked });
  };

  const handleResponseSize = (value) => {
    dispatch({ type: 'CREATE_SURVEY_SET_RESPONSE_SIZE', data: value });
  };

  const handleFormBricksUrl = (e) => {
    dispatch({ type: 'CREATE_SURVEY_SET_FORM_BRICKS_URL', data: e.target.value });
  };

  const handleSubmit = async () => {
    if (hasErrors()) return;
    if (isLoading) return;

    setStartDateError(false);
    setIsDateBeforeError(false);
    setEndDateError(false);
    setTitleError(false);

    try {
      if (startDateNew && endDateNew) {
        setIsLoading(true);
        let finalSurvey = {
          survey: Object.values(surveyInputData).map((q, i) => {
            return { ...q, id: i + 1 };
          }),
        };

        console.log({ finalSurvey });
        let validated = await validateObject(finalSurvey, formType, isQuiz);
        if (validated) {
          validated.organizationId = organizationId;
          validated.name = surveyName;
          validated.start = startDateNew;
          validated.end = endDateNew;
          validated.media = media;
          validated.formType = formType;
          validated.isLiveEvent = isLiveEvent;
          validated.isQuiz = isQuiz;
          validated.isResearch = isResearch;
          if (isQuiz) {
            validated.type = 'quiz';
          } else if (isLiveEvent && !isQuiz) {
            validated.type = 'live';
          } else if (isResearch) {
            validated.type = 'research';
          } else {
            validated.type = 'survey';
          }

          validated.isWelcomeMessageEnabled = isWelcomeMessage;
          validated.isClosingMessageEnabled = isClosingMessage;
          if (welcomeMessage && welcomeMessage !== '') validated.welcomeMessage = welcomeMessage;
          if (closingMessage && closingMessage !== '') validated.closingMessage = closingMessage;

          if (optInMessage && optInMessage !== '') validated.optInMessage = optInMessage;
          if (testimonialMessage && testimonialMessage !== '')
            validated.isTestimonialEnabled = isTestimonial;
          validated.testimonialMessage = testimonialMessage;

          validated.media = {};

          if (media && media.welcomeURL !== '') validated.media.welcomeURL = media.welcomeURL;

          if (media && media.closeURL !== '') validated.media.closeURL = media.closeURL;

          if (media && media.closingMessage !== '')
            validated.media.closingMessage = media.closingMessage;

          if (media && media.welcomeFileType !== '')
            validated.media.welcomeFileType = media.welcomeFileType;

          if (media && media.closingFileType !== '')
            validated.media.closingFileType = media.closingFileType;

          validated.settings = {};
          if (settings && settings.isRepeatable)
            validated.settings.isRepeatable = settings.isRepeatable;
          if (settings && settings.responseSize)
            validated.settings.responseSize = settings.responseSize;
          if (settings && settings.formBricksUrl)
            validated.settings.formBricksUrl = settings.formBricksUrl;

          if (dayjs().isSame(dayjs(startDateNew), 'day')) validated.status = 'active';

          //make sure that subHeading numbers align always with question numbers
          const sanetizedSurvey = recalculateSubHeadingNumbering(validated);

          console.log({ sanetizedSurvey })

          if (action === 'create') {
            const response = await axiosPrivate.post(apiURL, sanetizedSurvey);

            StatusNotification('success', 'Survey Submited');
            dispatch({ type: 'ADD_SURVEY', data: response.data });
          }
          if (action === 'edit') {
            const response = await axiosPrivate.put(
              '/api/surveys/edit/' + surveyId,
              sanetizedSurvey
            );

            StatusNotification('success', 'Survey Updated');
            // dispatch({ type: 'PROCESS_SURVEY', data: response.data });
          }

          setIsLoading(false);
          history.push('/surveys');
          dispatch({ type: 'CREATE_SURVEY_CLEAR' });
        }
      }
    } catch (error) {
      let errorMessage;

      if (error?.response?.status === '429') errorMessage = error?.response?.data?.message;
      else if (error?.data?.path) errorMessage = extractValidationError(error);
      else
        errorMessage =
          error?.response?.data?.message || error?.message || 'Please logout and login again';

      if (error) StatusNotification('error', 'Unable to submit form', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = () => {
    setModalVisible(true);
  };

  const hideModal = () => {
    setModalVisible(false);
  };

  const handleReset = () => {
    dispatch({ type: 'CREATE_SURVEY_CLEAR' });
    setTypeCounter({ openEnded: 1, optionList: 1, wordCount: 1 });
    setResetKey(Date.now());
    setStartDateError(false);
    setIsDateBeforeError(false);
    setEndDateError(false);
    setTitleError(false);
  };

  const onStartDateChange = (date) => {
    setStartDateError(false);
    setIsDateBeforeError(false);
    if (date.diff(new Date(), 'days') >= 0)
      // setStartDateNew(date.format('YYYY-MM-DD'));
      dispatch({
        type: 'CREATE_SURVEY_START_DATE',
        data: date.format('YYYY-MM-DD'),
      });
    else setStartDateError(true);
  };
  const onEndDateChange = (date) => {
    setIsDateBeforeError(false);
    setEndDateError(false);
    if (date.diff(new Date(), 'days') >= 0)
      // setEndDateNew(date.format('YYYY-MM-DD'));
      dispatch({
        type: 'CREATE_SURVEY_END_DATE',
        data: date.format('YYYY-MM-DD'),
      });
    else setEndDateError(true);
  };

  const addQuestion = () => {
    const newQuestionNumber = questions.length + 1;
    const newQuestion = { id: newQuestionNumber, text: '' };
    setQuestions([...questions, newQuestion]);

    // Update Redux store
    dispatch({
      type: 'CREATE_SURVEY_NUMBER_OF_QUESTIONS',
      data: newQuestionNumber,
    });

    dispatch({
      type: 'CREATE_SURVEY_INPUT_DATA',
      data: {
        ...surveyInputData,
        [newQuestionNumber]: {
          mainQuestion: '',
          options: [],
          type: 'optionList',
          subHeading: `Question ${newQuestionNumber}`,
        },
      },
    });

    dispatch({
      type: 'CREATE_SURVEY_COMPLETED',
      data: { ...completed, [newQuestionNumber]: false },
    });
  };

  const removeQuestion = (id) => {
    // Filter out the question from the local state
    const updatedQuestions = questions.filter((q) => q.id !== id);
    setQuestions(updatedQuestions);

    // Dispatch action to update the number of questions
    dispatch({ type: 'CREATE_SURVEY_NUMBER_OF_QUESTIONS', data: updatedQuestions.length });

    // Create a new surveyInputData object without the removed question
    const updatedSurveyInputData = { ...surveyInputData };
    delete updatedSurveyInputData[id];

    dispatch({
      type: 'CREATE_SURVEY_INPUT_DATA',
      data: updatedSurveyInputData,
    });

    // Create a new completed object without the removed question
    const updatedCompleted = { ...completed };
    delete updatedCompleted[id];

    dispatch({
      type: 'CREATE_SURVEY_COMPLETED',
      data: updatedCompleted,
    });
  };

  const updateQuestionText = (questionId, text) => {
    setQuestions(questions.map((q) => (q.id === questionId ? { ...q, text } : q)));
  };

  const moveQuestion = useCallback(
    (dragIndex, hoverIndex) => {
      setQuestions((prevQuestions) => {
        const newQuestions = update(prevQuestions, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevQuestions[dragIndex]],
          ],
        });

        const updatedSurveyInputData = {};
        const updatedEmojis = {};
        const updatedCompleted = {};
        const updatedOpenEndeds = {};

        newQuestions.forEach((question, index) => {
          const newKey = (index + 1).toString();
          const oldKey = question.id.toString();

          if (surveyInputData[oldKey]) {
            updatedSurveyInputData[newKey] = {
              ...surveyInputData[oldKey],
              subHeading: `${
                surveyInputData[oldKey].type === 'openEnded' ? 'Open Ended' : 'Question'
              } ${newKey}`,
            };
          }
          if (emojis[oldKey]) {
            updatedEmojis[newKey] = emojis[oldKey];
          }
          if (completed[oldKey]) {
            updatedCompleted[newKey] = completed[oldKey];
          }
          if (openEndeds[oldKey] !== undefined) {
            updatedOpenEndeds[newKey] = openEndeds[oldKey];
          }
        });

        dispatch({ type: 'CREATE_SURVEY_INPUT_DATA', data: updatedSurveyInputData });
        dispatch({ type: 'CREATE_SURVEY_SET_EMOJIS', data: updatedEmojis });
        dispatch({ type: 'CREATE_SURVEY_COMPLETED', data: updatedCompleted });
        dispatch({ type: 'CREATE_SURVEY_SET_OPEN_ENDEDS', data: updatedOpenEndeds });

        return newQuestions;
      });
    },
    [dispatch, surveyInputData, emojis, completed, openEndeds]
  );

  const activePanel = open !== null && open[0] <= questions.length ? open[0] : null;
  // Replace panelsArray generation with this
  const renderQuestions = () => {
    return questions.map((question, questionIndex) => (
      <DraggableCollapse
        key={question.id}
        id={question.id}
        index={questionIndex}
        moveItem={moveQuestion}
        header={
          <Space>
            <MenuOutlined style={{ cursor: 'move' }} />
            <span>
              {questionIndex + 1}:{' '}
              {question.text.length > 50 ? question.text.slice(0, 50) + '...' : question.text}
            </span>
          </Space>
        }
        extra={
          <Space>
            {completed[questionIndex + 1] === true
              ? tagComplete
              : !isObjectEmpty(completed)
              ? tagDraft
              : null}{' '}
            <MinusCircleOutlined onClick={() => removeQuestion(question.id)} />
          </Space>
        }
        activePanel={activePanel}
        setOpen={setOpen}
      >
        {formType === 'basic' ? (
          <SurveyFormBasic
            key={`${resetKey}-${question.id}`}
            question={question}
            surveyNumber={questionIndex + 1}
            updateQuestionText={updateQuestionText}
            removeQuestion={removeQuestion}
            setOpen={setOpen}
          />
        ) : (
          <SurveyFormAdvanced
            key={`${resetKey}-${question.id}`}
            question={question}
            surveyNumber={questionIndex + 1}
            updateQuestionText={updateQuestionText}
            removeQuestion={removeQuestion}
            setOpen={setOpen}
            typeCounter={typeCounter}
            setTypeCounter={setTypeCounter}
            numberOfQuestions={numberOfQuestions}
            questionType={surveyInputData[questionIndex + 1]?.type || 'optionList'}
            openEndeds={openEndeds} // Add this line
          />
        )}
      </DraggableCollapse>
    ));
  };

  let shouldRender = false;

  if (daysLeft > 0 && subscriptionStatus === 'trial') shouldRender = true;
  if (subscriptionStatus === 'active' && planType !== 'free') shouldRender = true;

  return shouldRender ? (
    <>
      <Row style={{ marginTop: 48 }}>
        <Col xs={24} md={12}>
          <SectionHeader
            title={action === 'create' ? 'Create Activity' : 'Edit Activity'}
            isFront={false}
            subtitle=''
          />
        </Col>
        <Col span={24} style={{ paddingBottom: 10 }}>
          <Row>
            <Col xs={24} md={12}>
              <Row>
                <Col span={24}>
                  <Input
                    value={surveyName}
                    onChange={handleSurveyTitle}
                    minLength={5}
                    maxLength={26}
                    showCount={true}
                    placeholder='Survey Title'
                    className='field-item form-field'
                  />
                </Col>
                <Col>
                  {titleError && (
                    <span style={{ color: 'red' }}>
                      Only survey name, With a length between 5 and 25 characters is allowed.
                    </span>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ paddingBottom: 10 }}>
          <Row>
            <Col xs={24} md={12}>
              <Row>
                <Col span={24}>
                  <Row>
                    <Col xs={24} md={12}>
                      <DatePicker
                        value={startDateNew ? dayjs(startDateNew) : null}
                        allowClear={false}
                        style={{ height: 40, width: '100%' }}
                        onChange={onStartDateChange}
                        placeholder='Start Date'
                        status={startDateError ? 'error' : null}
                      />
                    </Col>
                    <Col xs={24} md={12}>
                      <DatePicker
                        value={endDateNew ? dayjs(endDateNew) : null}
                        allowClear={false}
                        style={{ height: 40, width: '100%' }}
                        onChange={onEndDateChange}
                        placeholder='End Date'
                        status={endDateError ? 'error' : isDateBeforeError ? 'error' : null}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col>
                  {startDateError || endDateError ? (
                    <span style={{ color: 'red' }}>
                      Valid <strong>future</strong> dates required
                    </span>
                  ) : isDateBeforeError ? (
                    <span style={{ color: 'red' }}>
                      End date <strong>cannot</strong> be before start date
                    </span>
                  ) : null}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ paddingBottom: 10 }}>
          <Row>
            <Col span={24}>
              <Row align='bottom'>
                <Col xs={{ span: 24, order: 2 }} md={6}>
                  {' '}
                  {screenSize !== 'xs' && (
                    <Title level={5} style={{ top: 8 }}>
                      <Tag color={GlobalColors.lightPurple}>Number of questions</Tag>
                    </Title>
                  )}
                </Col>
                <Col
                  xs={{ span: 24, order: 1 }}
                  md={{ span: 6, order: 2 }}
                  style={{ textAlign: 'right' }}
                >
                  {action !== 'edit' && (
                    <>
                      <Tooltip
                        placement='top'
                        color={GlobalColors.mainPurple}
                        title={toolTip(
                          'Note: This feature is experimental, It takes around 1.5 minutes to complete, and sometime gives incorrect results 🚀'
                        )}
                      >
                        <Button
                          onClick={showModal}
                          style={{
                            cursor: 'pointer',
                            paddingLeft: 0,
                          }}
                          type='primary'
                          icon={
                            <Image
                              src={magic}
                              preview={false}
                              height={22}
                              style={{ marginLeft: 4, marginRight: 9 }}
                            />
                          }
                        >
                          Generate with AI
                          <Tag
                            color={GlobalColors.lightPurple}
                            style={{ marginLeft: 4, marginRight: 0 }}
                          >
                            beta
                          </Tag>
                        </Button>
                      </Tooltip>
                      <GenerateSurveys
                        visible={modalVisible}
                        onCancel={hideModal}
                        organizationId={organizationId}
                        surveyName={surveyName}
                        formType={formType}
                      />
                    </>
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col xs={24} md={12} style={{ paddingRight: 8 }}>
                  <Row>
                    <Col span={24}>
                      <QuestionerSlider />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12} style={{ paddingBottom: 10 }}>
          <Row gutter={[24, 0]} justify='space-between'>
            <Col xs={24} md={10}>
              <Row gutter={screenSize === 'xs' ? [0, 12] : [44, 12]} justify='space-between'>
                <Col xs={12} md={6} style={screenSize !== 'xs' ? { textAlign: 'end' } : {}}>
                  {' '}
                  <Title level={5}>
                    <Tag color={GlobalColors.lightPurple}>Type</Tag>
                  </Title>
                </Col>
                <Col xs={12} md={18}>
                  <Tooltip
                    placement='top'
                    color={GlobalColors.mainPurple}
                    title={toolTip(
                      'Choose form type, Note that Advanced forms cannot be auto-generated by AI'
                    )}
                  >
                    <Segmented
                      size={'small'}
                      style={{ borderRadius: 4 }}
                      value={formType}
                      options={
                        isLiveEvent || isQuiz
                          ? [
                              {
                                label: (
                                  <Row>
                                    <Col span={12} style={{ fontSize: 16 }}>
                                      Basic
                                    </Col>
                                  </Row>
                                ),
                                value: 'basic',
                              },
                            ]
                          : [
                              {
                                label: (
                                  <Row>
                                    <Col span={12} style={{ fontSize: 16 }}>
                                      Basic
                                    </Col>
                                  </Row>
                                ),
                                value: 'basic',
                              },
                              {
                                label: (
                                  <Row>
                                    <Col span={12} style={{ fontSize: 16 }}>
                                      Advanced
                                    </Col>
                                  </Row>
                                ),
                                value: 'advanced',
                              },
                            ]
                      }
                      onChange={handleFormType}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
            <Col
              xs={24}
              md={14}
              style={screenSize !== 'xs' ? { textAlign: 'right' } : { textAlign: 'right' }}
            >
              <Row gutter={[0, 12]}>
                <Col
                  xs={12}
                  md={6}
                  style={screenSize !== 'xs' ? { textAlign: 'right' } : { textAlign: 'left' }}
                >
                  {' '}
                  <Title level={5}>
                    <Tag color={GlobalColors.lightPurple}>Mode</Tag>
                  </Title>
                </Col>
                <Col xs={12} md={18} style={{ textAlign: 'left' }}>
                  <Tooltip
                    placement='top'
                    color={GlobalColors.mainPurple}
                    title={toolTip(
                      'Research = AI moderated survey, Quiz = Quiz survey, Live = Live presentation'
                    )}
                  >
                    <Segmented
                      size={'small'}
                      style={{ borderRadius: 4 }}
                      value={
                        isLiveEvent && isQuiz
                          ? 'quiz'
                          : isLiveEvent
                          ? 'live'
                          : isResearch
                          ? 'research'
                          : 'regular'
                      }
                      options={
                        formType === 'basic'
                          ? [
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Regular
                                    </Col>
                                  </Row>
                                ),
                                value: 'regular',
                              },
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Research
                                    </Col>
                                  </Row>
                                ),
                                value: 'research',
                              },
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Quiz
                                    </Col>
                                  </Row>
                                ),
                                value: 'quiz',
                              },
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Live
                                    </Col>
                                  </Row>
                                ),
                                value: 'live',
                              },
                            ]
                          : [
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Regular
                                    </Col>
                                  </Row>
                                ),
                                value: 'regular',
                              },
                              {
                                label: (
                                  <Row>
                                    <Col span={8} style={{ fontSize: 16 }}>
                                      Research
                                    </Col>
                                  </Row>
                                ),
                                value: 'research',
                              },
                            ]
                      }
                      onChange={handleMode}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row>
            <Col xs={24} md={12}>
              <DndProvider backend={HTML5Backend}>{renderQuestions()}</DndProvider>
            </Col>
          </Row>
          <Button onClick={addQuestion} icon={<PlusOutlined />}>
            Add Question
          </Button>
        </Col>
        <Col span={24} style={{ marginTop: 20 }}>
          <Row gutter={[8, 0]}>
            <Col md={12} xs={24}>
              <Row gutter={[8, 8]} justify='space-between'>
                <Tooltip
                  placement='top'
                  color={GlobalColors.mainPurple}
                  title={toolTip(
                    'You have the option to upload a picture or video as a banner that will be displayed when the first message is received within WhatsApp.'
                  )}
                >
                  <Col md={12} xs={24}>
                    {!titleError && surveyName && (
                      <Media
                        fileName={surveyName}
                        organizationId={organizationId}
                        usage={'Welcome'}
                        planType={planType}
                      />
                    )}
                  </Col>
                </Tooltip>
                <Tooltip
                  placement='top'
                  color={GlobalColors.mainPurple}
                  title={toolTip(
                    'You can choose to upload a picture or video as BONUS content provided the respondent reaches the end of the survey.'
                  )}
                >
                  <Col
                    md={12}
                    xs={24}
                    style={screenSize === 'xs' ? { textAlign: 'left' } : { textAlign: 'right' }}
                  >
                    {!titleError && surveyName && (
                      <Media
                        fileName={surveyName}
                        organizationId={organizationId}
                        usage={'Close'}
                        planType={planType}
                      />
                      // </Badge.Ribbon>
                    )}
                  </Col>
                </Tooltip>
                {!titleError && isCloseURL && surveyName && (
                  <Col span={24} style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <ClosingMessageInput />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ marginTop: 20 }}>
          <Row gutter={[8, 0]}>
            <Col md={12} xs={24}>
              <Collapse accordion>
                <Panel
                  header='Welcome & closing messages'
                  key='popia'
                  extra={<Tag color={GlobalColors.lightPurple}>optional</Tag>}
                >
                  <Col span={24}>
                    <Row gutter={[8, 0]}>
                      <Col span={24}>
                        {' '}
                        <Row gutter={[8, 0]}>
                          <Col md={12} xs={24}>
                            <Checkbox
                              checked={isWelcomeMessage}
                              onChange={handleWelcomeMessageCheckBox}
                            >
                              Welcome message
                            </Checkbox>{' '}
                          </Col>
                          <Col md={12} xs={24}>
                            <Checkbox
                              checked={isClosingMessage}
                              onChange={handleClosingMessageCheckBox}
                            >
                              Closing message
                            </Checkbox>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24} style={{ padding: 8 }}>
                        <Row gutter={[8, 0]}>
                          <Col md={12} xs={24}>
                            {isWelcomeMessage && (
                              <Input.TextArea
                                value={welcomeMessage}
                                onChange={handleWelcomeMessage}
                                rows={5}
                                placeholder={`Hello {{name}}, You are about to take part in the {{surveyName}} survey, The survey is _completely anonymous_ and your personal information will not be disclosed. Press *yes* if you are happy to continue 😄.`}
                                maxLength={350}
                                showCount
                              />
                            )}
                          </Col>
                          <Col md={12} xs={24}>
                            {isClosingMessage && (
                              <Input.TextArea
                                value={closingMessage}
                                onChange={handleClosingMessage}
                                rows={5}
                                placeholder={`Thank you for taking part in the {{surveyName}} survey. Cheers for now 😊.`}
                                maxLength={350}
                                showCount
                              />
                            )}
                          </Col>
                        </Row>{' '}
                      </Col>
                    </Row>
                  </Col>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ marginTop: 20 }}>
          <Row gutter={[8, 0]}>
            <Col md={12} xs={24}>
              <Collapse accordion>
                <Panel
                  header='POPIA Consent messages'
                  key='popia'
                  extra={<Tag color={GlobalColors.lightPurple}>optional</Tag>}
                >
                  <Col span={24} style={{ padding: 8 }}>
                    <Row gutter={[8, 0]}>
                      <Col span={24}>
                        {' '}
                        <Row gutter={[8, 0]}>
                          <Col md={12} xs={24}>
                            <Checkbox checked={isOptIn} onChange={handleOptInCheckBox}>
                              Marketing & communication opt-in
                            </Checkbox>{' '}
                          </Col>
                          <Col md={12} xs={24}>
                            <Checkbox checked={isTestimonial} onChange={handleTestimonialCheckBox}>
                              Use feedback for testimonial
                            </Checkbox>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row gutter={[8, 0]}>
                          <Col md={12} xs={24}>
                            {isOptIn && (
                              <Input.TextArea
                                value={optInMessage}
                                onChange={handleOptInMessage}
                                rows={5}
                                placeholder={`Type opt-in message e.g. Stay in the loop! Reply 'Yes' to receive exclusive updates and promotions. from {{organizationName}}`}
                                maxLength={150}
                                showCount
                              />
                            )}
                          </Col>
                          <Col md={12} xs={24}>
                            {isTestimonial && (
                              <Input.TextArea
                                value={testimonialMessage}
                                onChange={handleTestimonialMessage}
                                rows={5}
                                placeholder={`We appreciate your feedback! Can we use your survey comments as testimonials on {{organizationName}}'s social media or website? `}
                                maxLength={150}
                                showCount
                              />
                            )}
                          </Col>
                        </Row>{' '}
                      </Col>
                    </Row>
                  </Col>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </Col>
        <Col span={24} style={{ marginTop: 20 }}>
          <Row gutter={[8, 0]}>
            <Col md={12} xs={24}>
              <Collapse accordion>
                <Panel
                  header='Settings'
                  key='settings'
                  extra={<Tag color={GlobalColors.lightPurple}>optional</Tag>}
                >
                  <Col span={24} style={{ padding: 8 }}>
                    <Row gutter={[8, 0]}>
                      <Col span={24}>
                        {' '}
                        <Row gutter={[8, 0]}>
                          <Col md={12} xs={24}>
                            <Checkbox checked={isRepeatable} onChange={handleIsRepeatableCheckBox}>
                              isRepeatable
                            </Checkbox>{' '}
                          </Col>
                          <Col md={12} xs={24}>
                            <Tooltip
                              placement='top'
                              color={GlobalColors.mainPurple}
                              title={toolTip('-1 means unlimited.')}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  backgroundColor: GlobalColors.whitePurple,
                                  borderRadius: 4,
                                  padding: 2,
                                }}
                              >
                                <strong>Reseponse Size</strong>
                              </span>
                              <InputNumber
                                defaultValue={-1}
                                value={responseSize}
                                onChange={handleResponseSize}
                              />
                            </Tooltip>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <span
                          style={{
                            fontSize: 12,
                            backgroundColor: GlobalColors.whitePurple,
                            borderRadius: 4,
                            padding: 2,
                          }}
                        >
                          <strong>FormBricks Url</strong>
                        </span>
                        <Input
                          placeholder=''
                          value={formBricksUrl}
                          onChange={handleFormBricksUrl}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Panel>
              </Collapse>
            </Col>
          </Row>
        </Col>
        <Col md={12} xs={24} style={{ marginTop: 20, marginBottom: 20 }}>
          <Row gutter={[8, 12]}>
            <Col md={12} xs={24}>
              <Button
                type='primary'
                style={{ width: '100%' }}
                onClick={handleSubmit}
                disabled={Object.values(surveyInputData).length === 0}
              >
                {isLoading ? <LoadingIcon /> : 'Submit'}
              </Button>
            </Col>
            <Col md={12} xs={24}>
              <Button style={{ width: '100%' }} onClick={handleReset}>
                {'Reset'}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  ) : (
    <SubscriptionExpired />
  );
};

export default HandleSurvey;
